import { IsNotEmpty, IsPositive, Max, ValidateIf, IsDefined, Validate, ValidateNested } from 'class-validator';
import { computed, toJS } from 'mobx';
import * as moment from "moment";
import * as uuid from 'uuid';

import { ObjectUtils } from 'src/core/utils/object';
import { EntityModel } from 'src/core/services/data/models';
import { DataService, property } from 'src/core/services/data.service';
import { BookemZulu as Model, BookemZulu } from 'src/core/services/data/JayDataContext';
import { ProductValidation } from 'src/core/customValidator/productValidator';
import { DisplayNameValidation } from 'src/core/customValidator/displayNameValidator';
import { ContractsProductValidator } from 'src/core/customValidator/contracts-product-validator';
import { ChildrenProductValidator } from 'src/core/customValidator/children-product-validator';
import { TourItineraryValidator } from 'src/core/customValidator/tour-itinerary-validator';

export class ProductDataService extends DataService<typeof Model.Products.Logic.ViewModels.ProductItem, Model.Products.Logic.ViewModels.ProductItem, ProductItem> {
	protected map(entity: Model.Products.Logic.ViewModels.ProductItem): ProductItem {
		return new ProductItemModel(entity, this.productCodeRequired);
	}

	static make(context?: any): ProductDataService {
		return new ProductDataService(context, ctx => ctx.Products);
	}

	static id = -1;

	add(productType: string, defaultLanguage: string, parentProduct?: ProductItem): Promise<ProductItem> {
		const entity = new Model.Products.Logic.ViewModels.ProductItem();
		entity.id = ProductDataService.id--;
		entity.documentsLibraryId = uuid.v4();

		entity.productType = this.getProductType(productType);
		(entity as any).dynamicProperties['erratas'] = [];
		switch (productType) {
			case 'Accomodation':
				(entity as any).dynamicProperties['accomodation'] = {
					checkIn: moment.utc().hour(16).minute(0).second(0).toDate(),
					checkOut: moment.utc().hour(12).minute(0).second(0).toDate(),
				};
				break;
			case 'Transfer':
				(entity as any).dynamicProperties['transfer'] = {};
				break;
			case 'Flight':
				(entity as any).dynamicProperties['flight'] = {};
				break;
			case 'CarRent':
				break;
		}

		entity.childrenProducts = [];
		entity.itineraries = [];
		(entity as any).dynamicProperties['displayName'] = {
			[defaultLanguage]: ''
		};
		(entity as any).dynamicProperties['translations'] = {
			[defaultLanguage]: ''
		};
		if (parentProduct) {
			entity.parentProduct = parentProduct.entity;
			entity.hasParent = true;
			if (parentProduct.id > 0) {
				entity.parentProductId = parentProduct.id;
			}
			entity.location = parentProduct.location;
			entity.regionId = parentProduct.regionId;
			entity.placeId = parentProduct.placeId;
			(entity as any).dynamicProperties.maxPax = parentProduct.dynamicProperties.maxPax;
			const ageBands: AgeBandItem[] = parentProduct.dynamicProperties.ageBands || [];
			const customOccupancyConfigs: CustomOccupancyPax[] = parentProduct.dynamicProperties.customOccupancyConfigs || [];
			(entity as any).dynamicProperties.ageBands = ObjectUtils.deepCopy(ageBands, (prop) => prop === "__isProxy");
			(entity as any).dynamicProperties.customOccupancyConfigs = ObjectUtils.deepCopy(customOccupancyConfigs, (prop) => prop === "__isProxy");
		}

		return this.create(() => entity);
	}

	public productCodeRequired: boolean = false;

	protected getProductType(type: string): number {
		switch (type) {
			case 'General':
				return 0;
			case 'Accomodation':
				return 1;
			case 'CarRent':
				return 2;
			case 'Transfer':
				return 3;
			case 'Flight':
				return 4;
			case 'Room':
				return 5;
			case 'SupplementDiscount':
				return 6;
			case 'SalesAdjustment':
				return 7;
			case 'AccountingAdjustment':
				return 8;
			case 'Tour':
				return 9;
			case 'Package':
				return 10;
		}
	}
}

export class ProductItemModel extends EntityModel<Model.Products.Logic.ViewModels.ProductItem, ProductItem> implements ProductItem {
	productCodeRequired: boolean;
	constructor(entity: Model.Products.Logic.ViewModels.ProductItem, productCodeRequired: boolean = false) {
		super(entity);
		this.productCodeRequired = productCodeRequired;
	}

	@property<ProductItemModel>(o => o.entity.id, (o, value) => o.entity.id = value)
	public id: number;

	@property<ProductItemModel>(o => o.entity.parentProductId, (o, value) => o.entity.parentProductId = value)
	public parentProductId: number;

	@property<ProductItemModel>(o => o.entity.parentProduct, (o, value) => o.entity.parentProduct = value)
	public parentProduct: any;

	@property<ProductItemModel>(o => o.entity.code, (o, value) => o.entity.code = value)
	@ValidateIf(o => o.productCodeRequired)
	@IsNotEmpty()
	public code: string;

	@property<ProductItemModel>(o => o.entity.productType, (o, value) => o.entity.productType = value)
	public productType: BookemZulu.Core.Models.ProductType;

	@property<ProductItemModel>(o => o.entity.contractsDisplayNames, (o, value) => o.entity.contractsDisplayNames = value)
	public contractsDisplayNames: string[];

	@property<ProductItemModel>(o => o.entity.regionId, (o, value) => o.entity.regionId = value)
	@IsDefined({
		message: 'region should not be empty'
	})
	public regionId: number;

	@computed
	public get productTypeText(): string {
		switch (this.entity.productType) {
			case 0:
				return 'General';
			case 1:
				return 'Accomodation';
			case 2:
				return 'Car rent';
			case 3:
				return 'Transfer';
			case 4:
				return 'Flight';
			case 5:
				return 'Room';
			case 6:
				return 'Supplement/Discount';
			case 7:
				return 'Sales Adjustment';
			case 8:
				return 'Accounting Adjustment';
			case 9:
				return 'Tour';
			case 10:
				return 'Package';
		}
		return null;
	}

	@property<ProductItemModel>(o => o.entity.categoryId, (o, value) => o.entity.categoryId = value)
	@IsDefined({
		message: 'category should not be empty'
	})
	public categoryId: number;

	@property<ProductItemModel>(o => o.entity.category, (o, value) => o.entity.category = value)
	public category: ProductCategorySummary;

	@property<any>(o => o.entity.dynamicProperties, (o, value) => o.entity.dynamicProperties = value)
	@Validate(ProductValidation)
	@Validate(DisplayNameValidation, ['en'])
	public dynamicProperties: {};

	@property<ProductItemModel>(o => o.entity.stopSales, (o, value) => o.entity.stopSales = value)
	public stopSales?: Date;

	@property<ProductItemModel>(o => o.entity.denyCurrencyConversion, (o, value) => o.entity.denyCurrencyConversion = value)
	public denyCurrencyConversion: boolean;

	@property<ProductItemModel>(o => o.entity.contracts, (o, value) => o.entity.contracts = value)
	@ValidateNested()
	@Validate(ContractsProductValidator)
	public contracts: ContractItem[];

	@property<ProductItemModel>(o => o.entity.childrenProducts, (o, value) => o.entity.childrenProducts = value)
	@Validate(ChildrenProductValidator)
	@ValidateNested()
	public childrenProducts: ProductItem[];

	@property<ProductItemModel>(o => o.entity.itineraries, (o, value) => o.entity.itineraries = value)
	@Validate(TourItineraryValidator)
	@ValidateNested()
	public itineraries: TourItineraryItem[];

	@property<ProductItemModel>(o => o.entity.placeId, (o, value) => o.entity.placeId = value)
	public placeId?: number;

	@property<ProductItemModel>(o => o.entity.location, (o, value) => o.entity.location = value)
	public location: any;

	@property<ProductItemModel>(o => o.entity.hasParent, (o, value) => o.entity.hasParent = value)
	public hasParent: boolean;
	@property<ProductItemModel>(o => o.entity.documentsLibraryId, (o, value) => o.entity.documentsLibraryId = value)
	public documentsLibraryId: string;
	async validate(validateChildren: boolean, property?: string): Promise<ValidationError[]> {
		let result = await super.validate(validateChildren, property);
		if (validateChildren && this.contracts) {
			for (let contract of this.contracts) {
				if (contract.allotment && contract.allotment.validate) {
					const allotmentErrors = await contract.allotment.validate(true);
					result = [...result, ...allotmentErrors];
				}
			}
		}

		return result;
	}
}



// WEBPACK FOOTER //
// ./src/app/products/products/form/product-form.data.ts